/* Category definitions — each declares its own material groups and behavior.
   This is the differentiated IP of the app (HANDOFF.md §4): ported as-is,
   held in code rather than in a database table. */

export const CATEGORIES = {
  Sneakers: {
    key: "Sneakers",
    label: "Sneakers",
    icon: "👟",
    hasSizes: true,
    wearVerb: "Flight",
    wearVerbPlural: "Flights",
    dnaTitle: "Sneaker DNA",
    identityFields: { colorway: "Colorway", sku: "SKU / Style Code" },
    groups: [
      { key: "upper", title: "Upper materials (select all that apply)", options: ["Leather", "Patent Leather", "Mesh", "Suede", "Nubuck", "Canvas", "Knit", "Flyknit", "Gore-Tex", "Nylon", "Ripstop", "Other"] },
      { key: "midsole", title: "Midsole technology", options: ["Phylon", "Air", "Zoom Air", "React", "Boost", "EVA", "Lunarlon", "Fresh Foam", "Cloudfoam", "TPU", "Other"] },
      { key: "outsole", title: "Outsole material", options: ["Icy / Translucent Rubber", "Gum Rubber", "Continental Rubber", "Vibram", "Solid Rubber", "Crepe", "Leather Sole", "Cork", "Other"] },
    ],
    uvSensitive: (m) => (m.midsole || []).some((x) => ["EVA", "Phylon", "Boost"].includes(x)) || (m.outsole || []).some((x) => ["Gum Rubber", "Icy / Translucent Rubber"].includes(x)),
    oxidationSource: (m) => (m.outsole || []).some((x) => ["Gum Rubber", "Icy / Translucent Rubber"].includes(x)),
    delicate: (m) => (m.upper || []).some((x) => ["Suede", "Nubuck", "Patent Leather"].includes(x)),
    extraProfile: () => ({ "Glue Aging": "Monitor closely after Year 8" }),
  },
  Handbags: {
    key: "Handbags",
    label: "Handbags",
    icon: "👜",
    hasSizes: false,
    wearVerb: "Outing",
    wearVerbPlural: "Outings",
    dnaTitle: "Handbag DNA",
    identityFields: { colorway: "Color", sku: "Serial / Date Code" },
    groups: [
      { key: "exterior", title: "Exterior material (select all that apply)", options: ["Leather", "Patent Leather", "Saffiano Leather", "Caviar Leather", "Lambskin", "Exotic (Croc/Python)", "Coated Canvas", "Canvas", "Suede", "Nylon", "Denim", "Raffia", "Velvet", "Vegan", "Other"] },
      { key: "hardware", title: "Hardware finish", options: ["Gold-Tone", "Silver-Tone", "Palladium", "Brass", "Ruthenium", "Lacquered", "Other"] },
      { key: "lining", title: "Lining", options: ["Leather Lining", "Suede Lining", "Fabric Lining", "Microfiber Lining", "Other"] },
      { key: "closure", title: "Closure type", options: ["Zipper", "Turn-Lock", "Magnetic Snap", "Clasp / Kiss-Lock", "Buckle", "Open Top", "Other"] },
    ],
    uvSensitive: (m) => (m.exterior || []).some((x) => ["Lambskin", "Suede", "Velvet", "Raffia", "Exotic (Croc/Python)"].includes(x)),
    oxidationSource: (m) => (m.hardware || []).some((x) => ["Silver-Tone", "Brass"].includes(x)),
    delicate: (m) => (m.exterior || []).some((x) => ["Lambskin", "Suede", "Exotic (Croc/Python)", "Velvet", "Patent Leather"].includes(x)),
    extraProfile: (m) => ({
      "Shape Retention": "Store stuffed with acid-free paper; never hang by the handles",
      "Hardware Tarnish": (m.hardware || []).some((x) => ["Silver-Tone", "Brass"].includes(x)) ? "Elevated — use anti-tarnish strips" : "Low",
    }),
  },
  Purses: {
    key: "Purses",
    label: "Purses",
    icon: "👛",
    hasSizes: false,
    wearVerb: "Outing",
    wearVerbPlural: "Outings",
    dnaTitle: "Purse DNA",
    identityFields: { colorway: "Color", sku: "Serial / Date Code" },
    groups: [
      { key: "exterior", title: "Exterior material (select all that apply)", options: ["Leather", "Patent Leather", "Saffiano Leather", "Caviar Leather", "Lambskin", "Exotic (Croc/Python)", "Coated Canvas", "Canvas", "Suede", "Satin", "Velvet", "Vegan", "Other"] },
      { key: "hardware", title: "Hardware finish", options: ["Gold-Tone", "Silver-Tone", "Palladium", "Brass", "Ruthenium", "Lacquered", "Other"] },
      { key: "lining", title: "Lining", options: ["Leather Lining", "Suede Lining", "Fabric Lining", "Microfiber Lining", "Other"] },
      { key: "closure", title: "Closure type", options: ["Zipper", "Turn-Lock", "Magnetic Snap", "Clasp / Kiss-Lock", "Open Top", "Other"] },
    ],
    uvSensitive: (m) => (m.exterior || []).some((x) => ["Lambskin", "Suede", "Velvet", "Satin", "Exotic (Croc/Python)"].includes(x)),
    oxidationSource: (m) => (m.hardware || []).some((x) => ["Silver-Tone", "Brass"].includes(x)),
    delicate: (m) => (m.exterior || []).some((x) => ["Lambskin", "Suede", "Satin", "Velvet", "Exotic (Croc/Python)"].includes(x)),
    extraProfile: () => ({ "Shape Retention": "Store lightly stuffed; avoid compressing under heavier items" }),
  },
  Wallets: {
    key: "Wallets",
    label: "Wallets",
    icon: "💳",
    hasSizes: false,
    wearVerb: "Carry",
    wearVerbPlural: "Carries",
    dnaTitle: "Wallet DNA",
    identityFields: { colorway: "Color", sku: "Serial / Date Code" },
    groups: [
      { key: "exterior", title: "Exterior material (select all that apply)", options: ["Leather", "Patent Leather", "Saffiano Leather", "Caviar Leather", "Lambskin", "Exotic (Croc/Python)", "Coated Canvas", "Canvas", "Vegan", "Other"] },
      { key: "hardware", title: "Hardware finish", options: ["Gold-Tone", "Silver-Tone", "Palladium", "Brass", "Lacquered", "Other"] },
      { key: "lining", title: "Lining", options: ["Leather Lining", "Fabric Lining", "Microfiber Lining", "Other"] },
      { key: "closure", title: "Closure type", options: ["Zipper", "Snap", "Clasp / Kiss-Lock", "Open Top", "Other"] },
    ],
    uvSensitive: (m) => (m.exterior || []).some((x) => ["Lambskin", "Exotic (Croc/Python)"].includes(x)),
    oxidationSource: (m) => (m.hardware || []).some((x) => ["Silver-Tone", "Brass"].includes(x)),
    delicate: (m) => (m.exterior || []).some((x) => ["Lambskin", "Exotic (Croc/Python)", "Patent Leather"].includes(x)),
    extraProfile: () => ({ "Overstuffing Risk": "Avoid overfilling card slots — permanent stretching is the most common wallet failure" }),
  },
  Bags: {
    key: "Bags",
    label: "Bags",
    icon: "🎒",
    hasSizes: false,
    wearVerb: "Outing",
    wearVerbPlural: "Outings",
    dnaTitle: "Bag DNA",
    identityFields: { colorway: "Color", sku: "Serial / Style Code" },
    groups: [
      { key: "exterior", title: "Exterior material (select all that apply)", options: ["Leather", "Coated Canvas", "Canvas", "Nylon", "Ripstop", "Gore-Tex", "Denim", "Suede", "Vegan", "Other"] },
      { key: "hardware", title: "Hardware finish", options: ["Gold-Tone", "Silver-Tone", "Palladium", "Brass", "Ruthenium", "Other"] },
      { key: "lining", title: "Lining", options: ["Fabric Lining", "Microfiber Lining", "Leather Lining", "Other"] },
      { key: "closure", title: "Closure type", options: ["Zipper", "Buckle", "Magnetic Snap", "Turn-Lock", "Open Top", "Other"] },
    ],
    uvSensitive: (m) => (m.exterior || []).some((x) => ["Suede", "Denim", "Canvas"].includes(x)),
    oxidationSource: (m) => (m.hardware || []).some((x) => ["Silver-Tone", "Brass"].includes(x)),
    delicate: (m) => (m.exterior || []).some((x) => ["Suede", "Leather"].includes(x)),
    extraProfile: () => ({ "Strap Stress": "Rotate carry shoulders and avoid sustained overloading to prevent strap elongation" }),
  },
};

export const CATEGORY_KEYS = Object.keys(CATEGORIES);

export function categoryMeta(key) {
  return CATEGORIES[key] || CATEGORIES.Sneakers;
}

function buildSizeRange(start, end, suffix) {
  const arr = [];
  for (let s = start; s <= end + 0.001; s += 0.5) {
    arr.push(`${Number.isInteger(s) ? s : s.toFixed(1)}${suffix}`);
  }
  return arr;
}

export const SNEAKER_SIZES = [
  ...buildSizeRange(1, 10, "C"),
  ...buildSizeRange(10.5, 13.5, "C"),
  ...buildSizeRange(1, 7, "Y"),
  ...buildSizeRange(3.5, 18, ""),
];

export const AUTH_SOURCES = ["Retail / SNKRS", "StockX", "GOAT", "Consignment", "Flight Club", "Other"];
